import { Region } from '@components/common/constants';
import { experiment, FeatureFlagKeys } from '@lib/experiment';
import { apiAuthCaller } from '@services/apiCaller';
import { IRootStore } from '@stores/index';
import { action, makeObservable, observable } from 'mobx';

export type ClientFeature =
  | 'AUDIT_LOGS'
  | 'DISABLE_REPORTING'
  | 'TEAMS'
  | 'ETL'
  | 'ETL_ALLOWLIST'
  | 'BILLING'
  | 'BILLING_PAGE'
  | 'USAGE'
  | 'USAGE_PAGE'
  | 'ENFORCE_MFA'
  | 'MFA'
  | 'PAT'
  | 'DATA_MANAGEMENT'
  | 'AUDIENCES'
  | 'USER_DELETION'
  | 'SCIM'
  | 'EVENT_REPLAY'
  | 'DATA_RETENTION_STORAGE'
  | 'SOURCE_GATEWAY_DUMPS'
  | 'DATA_RETENTION_GATEWAY_DUMPS'
  | 'UPGRADE_BUTTON'
  | 'WORKSPACE_TOKEN'
  | 'DATA_PLANE_RATE_LIMIT'
  | 'COMMUNITY_SLACK'
  | 'GRAFANA'
  | 'QUICKSTART'
  | 'NOTIFICATION_SUBSCRIPTIONS'
  | 'PERMISSIONS'
  | 'MODELS'
  | 'TRACKING_PLAN'
  | 'DEVICE_MODE_TRANSFORMATIONS'
  | 'WAREHOUSE_SSH_TUNNELLING'
  | 'PLANS_PAGE'
  | 'SAVE_TRANSFORMATION_LIVE_EVENTS'
  | 'SHOW_CONTACT_US'
  | 'PROFILES'
  | 'SHOW_QUALIFIED_EXPERIENCE'
  | 'CHAT_WITH_SPECIALIST_IN_ONBOARDING'
  | 'FREE_TIER_PLAN'
  | 'SHOW_UPGRADE_FOR_WORKSPACES'
  | 'NEW_ROLES_FLOW'
  | 'DATA_RETENTION'
  | 'GEOLOCATION_ENRICHMENT'
  | 'DATA_CATALOG'
  | 'RETL_SYNC_TREND_GRAPH'
  | 'EVENT_AUDIT_API'
  | 'CREDENTIALS'
  | 'MTU_BILLING_ENABLED'
  | 'SERVICE_ACCESS_TOKENS'
  | 'DATA_CATALOG_CUSTOM_TYPES'
  | 'DATA_CATALOG_COPY_TP'
  | 'IS_PAID_PLAN'
  | 'ENTERPRISE_PLAN'
  | 'BOT_DETECTION'
  | 'BOT_EVENT_MANAGEMENT'
  | 'COHORT_AUDIENCES'
  | 'GRANULAR_PERMISSIONS'
  | 'PII_PERMISSIONS'
  | 'DEFAULT_PERMISSIONS'
  | 'GROUPS'
  | 'GROUPS_LIMIT'
  | 'EVENT_BLOCKING'
  | 'SNOWFLAKE_STREAMING'
  | 'EVENT_BLOCKING_LIMIT'
  | 'FBLA_SOURCE'
  | 'P95_LATENCY'
  | 'P95_LATENCY_ALERT'
  | 'SHOW_WORKSPACE_TOKENS_TAB'
  | 'DATA_GRAPH'
  | 'PLG_BILLING_V2'
  | 'RUDDER_AI_WEB';

export type ServerFeatures = {
  BILLING_TAB: boolean;
  PERMISSIONS: boolean;
  MFA: boolean;
  SHOW_UPGRADE: boolean;
  MODELS: boolean;
  EVENT_REPLAY: boolean;
  SHOW_JOIN_SLACK: boolean;
  DATA_RETENTION_GATEWAY_DUMPS: boolean;
  REVERSE_ETL: boolean;
  SHOW_TRIAL_LIMIT: boolean;
  GRAFANA_LOGIN: boolean;
  DEVICE_MODE_TRANSFORMATIONS: boolean;
  NOTIFICATIONS: boolean;
  AUDIT_LOGS: boolean;
  SHOW_WORKSPACE_TOKEN: boolean;
  DATA_RETENTION_STORAGE: boolean;
  DATA_RETENTION: boolean;
  QUICKSTART: boolean;
  USAGE_TAB: boolean;
  PLANS_TAB: boolean;
  ENFORCE_MFA: boolean;
  SHOW_LIVE_EVENTS_DISCLAIMER: boolean;
  OAUTH_DELETE: boolean;
  SCIM_PROVISIONING: boolean;
  BAA_AGREEMENT: boolean;
  PYTHON_TRANSFORMATIONS: boolean;
  ETL: boolean;
  ETL_ALLOWLIST: string[];
  AUDIENCES: boolean;
  PROFILES: boolean | undefined;
  SHOW_LIVE_EVENTS_LIMIT: boolean;
  PAT: boolean;
  DISABLE_REPORTING: boolean;
  SOURCE_GATEWAY_DUMPS: boolean;
  ETL_SOURCES_LIMIT: number;
  EVENT_STREAM_LIMIT: number;
  MAX_TEAM_COUNT: number | undefined;
  TRANSFORMATIONS_LIMIT: number;
  REVERSE_ETL_SOURCES_LIMIT: number;
  WAREHOUSE_TUNNELLING: boolean;
  SHOW_CONTACT_US: boolean;
  WORKSPACE_ROLES: 'BASIC' | 'EXPANDED' | undefined;
  CHAT_WITH_SPECIALIST_IN_ONBOARDING: boolean;
  PLAN_TYPE: 'freeTier' | 'OPEN_SOURCE' | 'starter' | 'growth' | 'pro' | 'enterprise';
  WORKSPACES_LIMIT: number;
  MULTI_WORKSPACES: boolean;
  TP_SOURCE_CONNECTIONS_LIMIT: number;
  GEOLOCATION_ENRICHMENT: boolean;
  RETL_SYNC_TREND_GRAPH: boolean;
  EVENT_AUDIT_API: boolean;
  RETL_CONNECTIONS_LIMIT: number | undefined;
  CREDENTIALS: boolean | undefined;
  BILLING_USAGE_TYPE: 'EVENTS' | 'MTU';
  TRACKING_PLANS_LIMIT: number;
  TRACKING_PLAN_EVENTS_LIMIT: number;
  TP_SOURCE_DROP_CONFIG: boolean;
  SERVICE_ACCESS_TOKENS: boolean | undefined;
  HIGH_GRANULARITY_WH_SYNC: boolean | undefined;
  DATA_CATALOG_CUSTOM_TYPES: boolean;
  DATA_CATALOG_CUSTOM_TYPES_LIMIT: number;
  BOT_DETECTION: boolean;
  BOT_EVENT_MANAGEMENT: boolean;
  GRANULAR_PERMISSIONS: boolean;
  PII_PERMISSIONS: boolean;
  DEFAULT_PERMISSIONS: boolean;
  GROUPS: boolean;
  GROUPS_LIMIT: number | undefined;
  EVENT_BLOCKING: boolean;
  EVENT_BLOCKING_LIMIT: number | undefined;
  SNOWFLAKE_STREAMING: boolean;
  FBLA_SOURCE: boolean | undefined;
  P95_LATENCY: boolean | undefined;
  P95_LATENCY_ALERT: boolean | undefined;
  DATA_GRAPH: boolean | undefined;
  RUDDER_AI_WEB: boolean | undefined;
};

type IFeatures = { [k in keyof ServerFeatures]?: ServerFeatures[k] };

type FeatureFlags = {
  [key: ClientFeature | FeatureFlagKeys | string]: boolean;
};

export interface IFeaturesStore {
  getFeatureConfiguration: (name: keyof ServerFeatures) => IFeatures[typeof name];
  getFeatures: () => Promise<void>;
  reset: () => void;
  firstLoad: boolean;
  allFeatures: IFeatures;
  getCombinedFeatureFlagsList: (destinationType?: string) => FeatureFlags;
  has: (name: ClientFeature) => boolean;
}

export class FeaturesStore implements IFeaturesStore {
  @observable protected rootStore: IRootStore;

  @observable private features: IFeatures = {};

  @observable public firstLoad = false;

  constructor(rootStore: IRootStore) {
    makeObservable(this);
    this.rootStore = rootStore;
  }

  @action.bound
  public getFeatureConfiguration(name: keyof ServerFeatures) {
    return this.features[name];
  }

  public get allFeatures() {
    return this.features;
  }

  @action.bound
  public reset() {
    this.features = {};
    this.firstLoad = false;
  }

  public async getFeatures() {
    const { workspaceStore } = this.rootStore;
    try {
      const response = await apiAuthCaller().get<IFeatures>('/featuresForPlan');
      this.features = response.data;
      workspaceStore.transformationsLimit = this.features.TRANSFORMATIONS_LIMIT as
        | number
        | undefined;
      if (this.has('ETL')) {
        workspaceStore.cloudSourcesLimit = this.features.ETL_SOURCES_LIMIT as number | undefined;
      }
      if (this.features.REVERSE_ETL) {
        workspaceStore.warehouseActionsLimit = this.features.REVERSE_ETL_SOURCES_LIMIT as
          | undefined
          | number;
      }
    } catch (err) {}
    this.firstLoad = true;
  }

  private isPlgBillingV2Enabled(): boolean {
    const { userStore } = this.rootStore;
    return (
      userStore.isOrgAdmin &&
      !!this.features.PLANS_TAB &&
      experiment.isFeatureEnabled(FeatureFlagKeys.enablePlgBillingV2)
    );
  }

  // Get a list of all server, client and amplitude feature flags
  // @param destinationType - optional parameter to get destination specific feature flags
  getCombinedFeatureFlagsList(destinationType?: string): FeatureFlags {
    const { userStore, workspaceStore, organizationStore } = this.rootStore;
    const trackingPlanEnabled = Boolean(this.features?.TP_SOURCE_CONNECTIONS_LIMIT);
    const profilesEnabled =
      !!this.features.PROFILES && experiment.isFeatureEnabled(FeatureFlagKeys.rs360EnableWht);

    // TODO: this is recalculated every to has is invoked, this could be cached instead of getting generated everytime
    // Hidden gates are expected to be plain booleans, so only expose raw plan
    // features that are already boolean and avoid truthy gates for limits/strings.
    const planFeatureFlags = Object.fromEntries(
      Object.entries(this.features).filter(([, value]) => typeof value === 'boolean'),
    );

    return {
      ...planFeatureFlags,
      AUDIT_LOGS: !!this.features.AUDIT_LOGS,
      DISABLE_REPORTING: !!this.features.DISABLE_REPORTING,
      TEAMS: this.features.MAX_TEAM_COUNT !== 0,
      BILLING: !!this.features.BILLING_TAB,
      BILLING_PAGE: userStore.isOrgAdmin && !!this.features.BILLING_TAB,
      USAGE: !!this.features.USAGE_TAB,
      USAGE_PAGE: userStore.isOrgAdmin && !!this.features.USAGE_TAB,
      MFA: userStore.canSetupMfa,
      ENFORCE_MFA: !!this.features.ENFORCE_MFA && userStore.isOrgAdmin,
      PAT: !!this.features.PAT,
      PLANS_PAGE: !!this.features.PLANS_TAB && userStore.isOrgAdmin,
      DATA_MANAGEMENT:
        (!!this.features.DATA_RETENTION || trackingPlanEnabled) && userStore.isOrgAdmin,
      DATA_RETENTION: !!this.features.DATA_RETENTION,
      AUDIENCES: !!this.features.AUDIENCES,
      COHORT_AUDIENCES:
        profilesEnabled && experiment.isFeatureEnabled(FeatureFlagKeys.enableCohortAudience),
      SCIM: !!this.features.SCIM_PROVISIONING,
      USER_DELETION: !!this.features.OAUTH_DELETE,
      EVENT_REPLAY: !!this.features.EVENT_REPLAY,
      DATA_RETENTION_STORAGE: !!this.features.DATA_RETENTION_STORAGE,
      SOURCE_GATEWAY_DUMPS: !!this.features.SOURCE_GATEWAY_DUMPS,
      DATA_RETENTION_GATEWAY_DUMPS: !!this.features.DATA_RETENTION_GATEWAY_DUMPS,
      UPGRADE_BUTTON: !!this.features.SHOW_UPGRADE,
      WORKSPACE_TOKEN: userStore.isOrgAdmin && !!this.features.SHOW_WORKSPACE_TOKEN,
      SHOW_WORKSPACE_TOKENS_TAB:
        userStore.isOrgAdmin &&
        !!this.features.SHOW_WORKSPACE_TOKEN &&
        experiment.isFeatureEnabled(FeatureFlagKeys.enableWorkspaceTokens),
      DATA_PLANE_RATE_LIMIT: !!this.features.SHOW_TRIAL_LIMIT,
      COMMUNITY_SLACK: !!this.features.SHOW_JOIN_SLACK,
      GRAFANA:
        !!this.features.GRAFANA_LOGIN && !!organizationStore.grafanaURL && userStore.isOrgAdmin,
      QUICKSTART:
        !!this.features.QUICKSTART && new Date(workspaceStore.createdAt) <= new Date('08/18/2022'),
      NOTIFICATION_SUBSCRIPTIONS: !!this.features.NOTIFICATIONS,
      SHOW_CONTACT_US: !!this.features.SHOW_CONTACT_US,
      PERMISSIONS: !!this.features.PERMISSIONS && userStore.isOrgAdmin,
      MODELS: !!this.features.MODELS,
      TRACKING_PLAN: trackingPlanEnabled,
      WAREHOUSE_SSH_TUNNELLING: !!this.features.WAREHOUSE_TUNNELLING,
      SAVE_TRANSFORMATION_LIVE_EVENTS: workspaceStore.defaultRegion === Region.US,
      DEVICE_MODE_TRANSFORMATIONS:
        workspaceStore.enableDeviceModeTransformations ||
        (!!this.features.DEVICE_MODE_TRANSFORMATIONS &&
          experiment.isFeatureEnabled(FeatureFlagKeys.deviceModeTransformations)),
      PROFILES: profilesEnabled,
      SHOW_QUALIFIED_EXPERIENCE: !!this.features.BILLING_TAB,
      CHAT_WITH_SPECIALIST_IN_ONBOARDING: !!this.features.CHAT_WITH_SPECIALIST_IN_ONBOARDING,
      FREE_TIER_PLAN: this.features.PLAN_TYPE === 'freeTier',
      IS_PAID_PLAN:
        this.features.PLAN_TYPE !== 'freeTier' && this.features.PLAN_TYPE !== 'OPEN_SOURCE',
      ENTERPRISE_PLAN: this.features.PLAN_TYPE === 'enterprise',
      SHOW_UPGRADE_FOR_WORKSPACES: this.features.WORKSPACES_LIMIT === 1,
      NEW_ROLES_FLOW: !!this.features.MULTI_WORKSPACES,
      ETL:
        !!this.features.ETL ||
        (!!this.features.ETL_ALLOWLIST && this.features.ETL_ALLOWLIST.length > 0),
      GEOLOCATION_ENRICHMENT: !!this.features.GEOLOCATION_ENRICHMENT,
      DATA_CATALOG: trackingPlanEnabled,
      RETL_SYNC_TREND_GRAPH: !!this.features.RETL_SYNC_TREND_GRAPH,
      EVENT_AUDIT_API: !!this.features.EVENT_AUDIT_API,
      CREDENTIALS: !!this.features.CREDENTIALS,
      MTU_BILLING_ENABLED: this.features.BILLING_USAGE_TYPE === 'MTU',
      SERVICE_ACCESS_TOKENS: !!this.features.SERVICE_ACCESS_TOKENS,
      HIGH_GRANULARITY_WH_SYNC: !!this.features.HIGH_GRANULARITY_WH_SYNC,
      DATA_CATALOG_CUSTOM_TYPES: !!this.features.DATA_CATALOG_CUSTOM_TYPES,
      DATA_CATALOG_COPY_TP: experiment.isFeatureEnabled(
        FeatureFlagKeys.enabledDataCatalogTPWorkspaceCopy,
      ),
      BOT_DETECTION: !!(
        this.features.BOT_DETECTION &&
        experiment.isFeatureEnabled(FeatureFlagKeys.enableBotBlocking)
      ),
      BOT_EVENT_MANAGEMENT: !!(
        this.features.BOT_EVENT_MANAGEMENT &&
        this.features.BOT_DETECTION &&
        experiment.isFeatureEnabled(FeatureFlagKeys.enableBotBlocking)
      ),
      GRANULAR_PERMISSIONS: !!this.features.GRANULAR_PERMISSIONS,
      PII_PERMISSIONS: !!this.features.PII_PERMISSIONS,
      DEFAULT_PERMISSIONS: !!this.features.DEFAULT_PERMISSIONS,
      GROUPS: !!this.features.GROUPS,
      EVENT_BLOCKING: !!this.features.EVENT_BLOCKING,
      P95_LATENCY: !!this.features.P95_LATENCY,
      P95_LATENCY_ALERT: !!this.features.P95_LATENCY_ALERT,
      DATA_GRAPH:
        !!this.features.DATA_GRAPH && experiment.isFeatureEnabled(FeatureFlagKeys.enableDataGraph),
      PLG_BILLING_V2: this.isPlgBillingV2Enabled(),
      RUDDER_AI_WEB: !!(
        this.features.RUDDER_AI_WEB &&
        experiment.isFeatureEnabled(FeatureFlagKeys.enableRudderAiWebChat)
      ),
      // Used by form builders prerequisites to conditionally show/hide form fields
      ...experiment.getAllFeatureFlags(),
      ...(destinationType && experiment.getAllDestinationFeatureFlags(destinationType)),
    };
  }

  has(name: ClientFeature): boolean {
    const clientFeatureAndExperiments = this.getCombinedFeatureFlagsList();
    return Boolean(clientFeatureAndExperiments[name]);
  }
}
